import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Plus, CheckCircle2 } from 'lucide-react';
import { CustomSymptomForm } from './CustomSymptomForm';
import { CategoryForm } from './CategoryForm';
import type { HDType } from '@/lib/types';
import { SYMPTOMS_BY_TYPE } from '@/lib/types';

interface SymptomSelectorProps {
  selectedSymptoms: string[];
  onSymptomToggle: (symptom: string) => void;
  energyType: HDType;
  userId: string;
  showCustomForm: boolean;
  onShowCustomForm: (show: boolean) => void;
}

type SymptomCategory = 'general' | 'emotionnel';

const SYMPTOM_CATEGORIES: Record<SymptomCategory, {
  label: string;
  description: string;
  color: string;
}> = {
  general: {
    label: 'Général',
    description: 'Vue d\'ensemble de votre état',
    color: 'bg-purple-100 text-purple-800 hover:bg-purple-200'
  },
  emotionnel: {
    label: 'Émotionnel',
    description: 'Ressentis émotionnels et états d\'âme',
    color: 'bg-pink-100 text-pink-800 hover:bg-pink-200'
  }
};

export function SymptomSelector({
  selectedSymptoms,
  onSymptomToggle,
  energyType,
  userId,
  showCustomForm,
  onShowCustomForm
}: SymptomSelectorProps) {
  const [selectedCategories, setSelectedCategories] = useState<SymptomCategory[]>(['general']);
  const [expandedCategory, setExpandedCategory] = useState<SymptomCategory>('general');

  const handleCategoryToggle = (category: SymptomCategory) => {
    setSelectedCategories(prev => {
      const isSelected = prev.includes(category);
      if (isSelected) {
        // If deselecting the current expanded category, switch to general
        if (expandedCategory === category) {
          setExpandedCategory('general');
        }
        return prev.filter(c => c !== category);
      } else {
        setExpandedCategory(category);
        return [...prev, category];
      }
    });
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-lg font-semibold mb-4">Que ressens-tu aujourd'hui ?</h2>
        <div className="flex flex-wrap gap-2 mb-6">
          {Object.entries(SYMPTOM_CATEGORIES).map(([key, category]) => (
            <Button
              key={key}
              variant="outline"
              className={`rounded-full transition-all duration-300 ${
                selectedCategories.includes(key as SymptomCategory)
                  ? category.color
                  : 'hover:bg-gray-100'
              }`}
              onClick={() => handleCategoryToggle(key as SymptomCategory)}
            >
              #{category.label}
            </Button>
          ))}
        </div>

        {/* Category Forms */}
        <div className="space-y-4">
          {Object.keys(SYMPTOM_CATEGORIES).map((key) => (
            <CategoryForm
              key={key}
              category={key}
              isOpen={expandedCategory === key}
              onClose={() => setExpandedCategory('general')}
              selectedSymptoms={selectedSymptoms}
              onSymptomToggle={onSymptomToggle}
            />
          ))}
        </div>
      </div>

      {/* Only show symptoms if general category is selected */}
      {expandedCategory === 'general' && (
        <div className="space-y-4">
          <h2 className="text-lg font-semibold">Coche ce qui te parle :</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {SYMPTOMS_BY_TYPE[energyType].map((symptom, index) => (
              <label
                key={index}
                className={`
                  flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors
                  ${selectedSymptoms.includes(symptom.text)
                    ? 'bg-[#54075B]/10 border-[#54075B]'
                    : 'bg-white hover:bg-gray-50 border-gray-200'}
                `}
              >
                <input
                  type="checkbox"
                  checked={selectedSymptoms.includes(symptom.text)}
                  onChange={() => onSymptomToggle(symptom.text)}
                  className="hidden"
                />
                <div className={`
                  w-5 h-5 rounded-full border-2 flex items-center justify-center
                  ${selectedSymptoms.includes(symptom.text)
                    ? 'border-[#54075B] bg-[#54075B]'
                    : 'border-gray-300'}
                `}>
                  {selectedSymptoms.includes(symptom.text) && (
                    <CheckCircle2 className="w-4 h-4 text-white" />
                  )}
                </div>
                <span className="flex-1">{symptom.text}</span>
              </label>
            ))}

            {/* Custom Symptom Option */}
            <div
              className={`
                flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors
                ${showCustomForm ? 'bg-[#54075B]/10 border-[#54075B]' : 'bg-white hover:bg-gray-50 border-gray-200'}
              `}
              onClick={() => onShowCustomForm(true)}
            >
              <div className={`
                w-5 h-5 rounded-full border-2 flex items-center justify-center
                ${showCustomForm ? 'border-[#54075B] bg-[#54075B]' : 'border-gray-300'}
              `}>
                <Plus className={`w-4 h-4 ${showCustomForm ? 'text-white' : 'text-gray-400'}`} />
              </div>
              <span className="flex-1">Autre (décrire votre ressenti)</span>
            </div>
          </div>

          {/* Custom Symptom Form */}
          {showCustomForm && (
            <CustomSymptomForm
              onClose={() => onShowCustomForm(false)}
              onSymptomAdded={(symptomText) => {
                onSymptomToggle(`Autre : ${symptomText}`);
                onShowCustomForm(false);
              }}
              energyType={energyType}
              userId={userId}
            />
          )}
        </div>
      )}
    </div>
  );
}